import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { MessageSquare, X, Loader2, Check } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { toast } from 'sonner';
import { getMessagesPrivacy, setMessagesPrivacy } from '../lib/api';

/**
 * "Who can message you" setting card + modal.
 * Backed by GET/PUT `/api/messages/privacy`.
 */
export default function MessagesPrivacyButton() {
  const { isDark } = useTheme();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('mutuals');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Lazy-load current value on first open.
  useEffect(() => {
    if (!open) return;
    let cancel = false;
    setLoading(true);
    getMessagesPrivacy()
      .then((res) => {
        if (!cancel) setValue(res.data?.messages_privacy || 'mutuals');
      })
      .catch(() => {})
      .finally(() => !cancel && setLoading(false));
    return () => {
      cancel = true;
    };
  }, [open]);

  const choose = async (next) => {
    if (next === value || saving) return;
    const prev = value;
    setValue(next); // optimistic
    setSaving(true);
    try {
      await setMessagesPrivacy(next);
      toast.success(t('messages.privacySaved'));
    } catch (err) {
      setValue(prev);
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const subtitle = {
    mutuals: t('messages.privacyMutuals'),
    everyone: t('messages.privacyEveryone'),
    nobody: t('messages.privacyNobody'),
  }[value];

  const OPTIONS = [
    { key: 'mutuals', label: t('messages.privacyMutuals') },
    { key: 'everyone', label: t('messages.privacyEveryone') },
    { key: 'nobody', label: t('messages.privacyNobody') },
  ];

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`w-full glass-card-sm p-4 flex items-center gap-3 transition-colors ${
          isDark ? 'text-white hover:bg-white/5' : 'text-gray-900 hover:bg-gray-50'
        }`}
        data-testid="messages-privacy-btn"
      >
        <MessageSquare className="w-5 h-5 opacity-70" />
        <div className="flex-1 text-left min-w-0">
          <div className="font-medium">{t('messages.privacyTitle')}</div>
          <div className={`text-xs truncate ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            {subtitle}
          </div>
        </div>
      </button>

      {open &&
        createPortal(
          <div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4"
            onClick={() => setOpen(false)}
            data-testid="messages-privacy-overlay"
          >
            <div
              className={`w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl ${
                isDark ? 'bg-[#1a1a1a] border border-white/10' : 'bg-white border border-gray-200'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={`px-5 py-4 flex items-center justify-between border-b ${
                  isDark ? 'border-white/10' : 'border-gray-100'
                }`}
              >
                <div className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {t('messages.privacyTitle')}
                </div>
                <button
                  onClick={() => setOpen(false)}
                  data-testid="messages-privacy-close"
                  className={isDark ? 'text-white/60' : 'text-gray-400'}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 space-y-2">
                {loading ? (
                  <div className="py-8 flex justify-center">
                    <Loader2
                      className={`w-5 h-5 animate-spin ${isDark ? 'text-white/60' : 'text-gray-400'}`}
                    />
                  </div>
                ) : (
                  OPTIONS.map((opt) => {
                    const active = value === opt.key;
                    return (
                      <button
                        key={opt.key}
                        onClick={() => choose(opt.key)}
                        disabled={saving}
                        data-testid={`messages-privacy-option-${opt.key}`}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl text-left text-sm ${
                          active
                            ? isDark
                              ? 'bg-[#E8334A]/15 border border-[#E8334A]/40 text-white'
                              : 'bg-[#E8334A]/10 border border-[#E8334A]/30 text-gray-900'
                            : isDark
                            ? 'bg-white/5 border border-transparent text-white'
                            : 'bg-gray-50 border border-transparent text-gray-900'
                        }`}
                      >
                        <span className="flex-1">{opt.label}</span>
                        {active && <Check className="w-4 h-4 text-[#E8334A] flex-shrink-0" />}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
